import { Box, Button, Dialog, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useCreatePreference } from '../../services/react-query/preferences/useCreatePreference';
import { useGetPreferenceById } from '../../services/react-query/preferences/useGetPreferencesById';
import { useUpdatePreference } from '../../services/react-query/preferences/useUpdatePreference';
import { queryClient } from '../../services/react-query/queryClient';
import { queryKeys } from '../../services/react-query/queryKeys';
import { dialogActionsStyles, headerTypographyProps, inputDividerStyling } from '../../styles/sxStyling';
import { PREFERENCES_VALID_KEYS, Preference } from '../../types/Preference';
import { Loading } from '../../uikit';
import { errorPopulator } from '../../utility/inputUtils';
import PreferenceColorField from './PreferenceColorField';

interface IPreferenceFormModalProps {
  uuid: string;
  preferenceKey: PREFERENCES_VALID_KEYS;
  isOpen: boolean;
  onCancel: () => void;
  setPreferenceSnackbarMessage: Dispatch<SetStateAction<string | null>>;
}

export default function PreferenceFormModal({ uuid, onCancel, setPreferenceSnackbarMessage, preferenceKey, isOpen }: IPreferenceFormModalProps) {
  const [preference, setPreference] = useState<Preference | null>(null);
  const { data: preferenceData, isLoading: isGetPreferenceByIdLoading } = useGetPreferenceById({ uuid });

  useEffect(() => {
    if (!isOpen) return;

    if (uuid === 'new') {
      setPreference({
        uuid: 'new',
        key: preferenceKey,
        value: '',
        description: '',
        color: null,
      } as unknown as Preference);
      return;
    }

    if (preferenceData) {
      setPreference(preferenceData);
    }
  }, [isOpen, uuid, preferenceKey, preferenceData]);

  const saveCallback = () => {
    queryClient.invalidateQueries({
      queryKey: [queryKeys.allPreferences, { filters: { keys: [preferenceKey] } }],
    });
    setPreferenceSnackbarMessage('Preference saved successfully!');
    onCancel();
  };

  const { mutate: createPreference } = useCreatePreference({ onSuccessCallback: saveCallback });
  const { mutate: updatePreference } = useUpdatePreference({ uuid, onSuccessCallback: saveCallback });

  if (isGetPreferenceByIdLoading && uuid !== 'new') {
    return <Loading />;
  }

  const updatePreferenceData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setPreference((prev) => (prev ? ({ ...prev, [id]: value } as Preference) : prev));
  };

  const handleOnAdd = () => {
    if (!preference) return;

    const payload = {
      key: preferenceKey,
      value: preference.value,
      description: preference.description,
      color: preference.color,
    };

    if (uuid === 'new') {
      createPreference(payload as any);
    } else {
      updatePreference(payload as any);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} fullWidth>
      <Box sx={{ padding: { xs: 0.5, md: 2 } }}>
        <Typography {...headerTypographyProps} sx={{ m: 1 }}>
          {`${uuid === 'new' ? 'Add' : 'Update'} Preference`}
        </Typography>
        <DialogContent sx={{ padding: { xs: '0' } }}>
          <Box sx={inputDividerStyling}>
            <TextField
              key="value"
              id="value"
              label="Value"
              autoFocus
              onChange={updatePreferenceData}
              value={preference?.value ?? ''}
              {...errorPopulator(preference, 'value')}
            />
            <TextField
              key="description"
              id="description"
              label="Description"
              onChange={updatePreferenceData}
              value={preference?.description ?? ''}
              {...errorPopulator(preference, 'description')}
            />

            <Box>
              <PreferenceColorField
                value={preference?.color ?? ''}
                onChange={(hex) => setPreference((prev) => (prev ? ({ ...prev, color: hex && hex.length ? hex : null } as Preference) : prev))}
                helperText="Pick a color or enter a hex like #aabbcc"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={dialogActionsStyles}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleOnAdd}>
            Save
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
